import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useSWRConfig } from "swr";
import {
  GetAccount200ResponsePositionsInner,
  GetOrderbook200Response,
} from "../../codegen-api";
import { IMarketPrecisionGetter } from "../../contexts/MarketInstrumentContext";
import { normalizeDecimalString } from "../../utils/decimalString";
import { estimateClosePositionFromOrderbook } from "../../utils/positionClose";
import { emitTradeExecuted } from "../../utils/tradeEvents";
import { IBaseModalProps } from "../BaseModal";
import { alignCloseLimitPrice } from "./ClosePositionModal.perpAction";
import { ClosePositionPageEnum } from "./ClosePositionModal.types";

interface IUseClosePositionStateParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  orderbookData: GetOrderbook200Response | undefined;
  getMarketPrecision: IMarketPrecisionGetter;
  show: IBaseModalProps["show"];
  isPerp: boolean;
  setCloseMode: Dispatch<SetStateAction<"market" | "limit">>;
  setCloseSizeInput: Dispatch<SetStateAction<string>>;
  setLimitPriceInput: Dispatch<SetStateAction<string>>;
  mutateCache: ReturnType<typeof useSWRConfig>["mutate"];
  revalidationTimersRef: MutableRefObject<number[]>;
  lastRevalidationRef: MutableRefObject<number>;
  availablePages: ClosePositionPageEnum[];
  pageIndex: number;
  onHide: IBaseModalProps["onHide"];
  setShowClosePositionConfirmation: Dispatch<SetStateAction<boolean>>;
  setShowPnLCard: Dispatch<SetStateAction<boolean>>;
}

export const useClosePositionState = ({
  position,
  orderbookData,
  getMarketPrecision,
  show,
  isPerp,
  setCloseMode,
  setCloseSizeInput,
  setLimitPriceInput,
  mutateCache,
  revalidationTimersRef,
  lastRevalidationRef,
  availablePages,
  pageIndex,
  onHide,
  setShowClosePositionConfirmation,
  setShowPnLCard,
}: IUseClosePositionStateParams) => {
  const closeEstimate = useMemo(
    () => estimateClosePositionFromOrderbook(position, orderbookData),
    [position, orderbookData]
  );

  const closePrecision = useMemo(
    () =>
      position
        ? getMarketPrecision(position.asset, position.instrument_type)
        : {
            price_step: 0.01,
            price_precision: 2,
            amount_step: 0.0001,
            amount_precision: 4,
          },
    [getMarketPrecision, position]
  );

  // Seed the size (full position) and limit price (mark/avg) when the modal opens.
  useEffect(() => {
    if (show && position && isPerp) {
      setCloseMode("market");
      setCloseSizeInput(
        normalizeDecimalString(position.amount) ?? position.amount
      );
      const seed =
        Number(position.mark_price) || Number(position.avg_entry_price) || 0;
      // On `price_step`, on the side the close order will be submitted with.
      setLimitPriceInput(
        seed ? alignCloseLimitPrice(String(seed), position.side, closePrecision) : ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, position?.instrument_id, position?.side]);

  const revalidateAfterPositionClose = useCallback(() => {
    // Bus only — each data owner converges itself. See `usePerpOrder`.
    emitTradeExecuted();
  }, []);


  const page = useMemo(
    () => availablePages[pageIndex >= availablePages.length ? 0 : pageIndex],
    [availablePages, pageIndex]
  );

  const onHideModal = useCallback(() => {
    onHide?.();
    setShowClosePositionConfirmation(false);
    setTimeout(() => {
      setShowPnLCard(false);
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHide]);

  const onOpenPositionConfirmationModal = useCallback(() => {
    setShowClosePositionConfirmation(true);
  }, []);

  const onClosePositionConfirmationModal = useCallback(() => {
    setShowClosePositionConfirmation(false);
  }, []);

  const onOpenPnLCard = useCallback(() => {
    setShowPnLCard(true);
  }, []);

  const onHidePnLCard = useCallback(() => {
    setShowPnLCard(false);
  }, []);

  return {
    closeEstimate,
    closePrecision,
    revalidateAfterPositionClose,
    page,
    onHideModal,
    onOpenPositionConfirmationModal,
    onClosePositionConfirmationModal,
    onOpenPnLCard,
    onHidePnLCard,
  };
};
